import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { getSupabase } from './client';
import type { Tables } from './database.types';
import type { WorkspaceScope } from './workspace';

export type RealtimeStatus = 'idle' | 'connecting' | 'live' | 'error';

export interface RecordChange {
  readonly module: string;
  readonly id: string;
  readonly deleted: boolean;
  readonly updatedBy: string | null;
  readonly isInsert: boolean;
}

export interface WorkspaceRealtimeHandlers {
  onRecord?: (change: RecordChange) => void;
  onSettings?: () => void;
  onMembers?: () => void;
  onStatus?: (status: RealtimeStatus) => void;
}

export interface WorkspaceRealtimeOptions {
  /** Subscribe to module records (the dashboard's "realtime" sync mode). */
  readonly records: boolean;
  /** Subscribe to settings/membership changes (always on in the dashboard). */
  readonly config: boolean;
}

type RecordRowPayload = RealtimePostgresChangesPayload<Tables<'records'>>;

let channelSeq = 0;

/**
 * One channel per scope. Payload bodies are ignored on purpose: records can carry large
 * Base64 attachments, so handlers receive keys only and re-read the row (RLS-checked).
 */
export function subscribeWorkspace(
  scope: WorkspaceScope,
  handlers: WorkspaceRealtimeHandlers,
  options: WorkspaceRealtimeOptions,
): () => void {
  const supabase = getSupabase();
  channelSeq += 1;
  const channel: RealtimeChannel = supabase.channel(`ws:${scope.workspaceId}:${scope.appKey}:${channelSeq}`);
  const filter = `workspace_id=eq.${scope.workspaceId}`;

  if (options.records) {
    channel.on('postgres_changes', { event: '*', schema: 'public', table: 'records', filter }, (payload: RecordRowPayload) => {
      const row = payload.eventType === 'DELETE' ? payload.old : payload.new;
      if (!row || row.app_key !== scope.appKey || typeof row.module !== 'string' || typeof row.id !== 'string') return;
      handlers.onRecord?.({
        module: row.module,
        id: row.id,
        deleted: payload.eventType === 'DELETE' || row.deleted === true,
        updatedBy: typeof row.updated_by === 'string' ? row.updated_by : null,
        isInsert: payload.eventType === 'INSERT',
      });
    });
  }

  if (options.config) {
    channel
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workspace_settings', filter }, () => {
        handlers.onSettings?.();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workspace_members', filter }, () => {
        handlers.onMembers?.();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'member_app_access', filter }, () => {
        handlers.onMembers?.();
      });
  }

  handlers.onStatus?.('connecting');
  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') handlers.onStatus?.('live');
    else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') handlers.onStatus?.('error');
    else if (status === 'CLOSED') handlers.onStatus?.('idle');
  });

  return () => {
    void supabase.removeChannel(channel);
  };
}
